import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeft, Info, Loader2, Save } from 'lucide-react';
import { useEffect } from 'react';
import {
  useForm,
  type Control,
  type FieldPath,
  type FieldValues,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { PartPicker } from '@/components/ui/part-picker';
import { Skeleton } from '@/components/ui/skeleton';
import { VesselSelect } from '@/components/ui/vessel-select';
import { usePart } from '@/features/parts/queries';
import {
  useCreateStockLot,
  useStockLot,
  useUpdateStockLot,
} from '@/features/stock-lots/queries';
import {
  stockLotCreateSchema,
  stockLotUpdateSchema,
  type StockLotCreateValues,
  type StockLotUpdateValues,
} from '@/features/stock-lots/schema';
import { type StockLot } from '@/features/stock-lots/types';
import { useVessel } from '@/features/vessels/queries';
import { vesselDisplayName } from '@/features/vessels/types';
import { ApiError } from '@/lib/api';
import { cn } from '@/lib/utils';

/** Generic TextField — works for create + update shapes. */
function TextField<TForm extends FieldValues>({
  control,
  name,
  label,
  hint,
  type = 'text',
  textarea = false,
}: {
  control: Control<TForm>;
  name: FieldPath<TForm>;
  label: string;
  hint?: string;
  type?: 'text' | 'number';
  textarea?: boolean;
}) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel>{label}</FormLabel>
          <FormControl>
            {textarea ? (
              <textarea
                {...field}
                rows={3}
                value={
                  field.value === null || field.value === undefined
                    ? ''
                    : String(field.value)
                }
                className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              />
            ) : (
              <Input
                type={type}
                {...field}
                value={
                  field.value === null || field.value === undefined
                    ? ''
                    : String(field.value)
                }
              />
            )}
          </FormControl>
          {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

function ReadOnlyField({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div>
      <p className="text-sm font-medium">{label}</p>
      <p className="mt-1 rounded-md border bg-muted/40 px-3 py-2 text-sm">
        {value}
      </p>
    </div>
  );
}

function CreateBranch({
  defaultPartId,
  defaultVesselId,
}: {
  defaultPartId: string | null;
  defaultVesselId: string | null;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const createMut = useCreateStockLot();

  const form = useForm<StockLotCreateValues>({
    resolver: zodResolver(stockLotCreateSchema),
    defaultValues: {
      partId: defaultPartId ?? '',
      vesselId: defaultVesselId ?? '',
      location: '',
      minStockOverride: null,
      maxStockOverride: null,
      notes: null,
    },
  });

  const errorMessage = (() => {
    if (!createMut.error) return null;
    if (createMut.error instanceof ApiError) {
      return t(createMut.error.key, {
        defaultValue: t('stockLot.form.saveError'),
        ...(createMut.error.params ?? {}),
      });
    }
    return t('stockLot.form.saveError');
  })();

  const onSubmit = form.handleSubmit((values) => {
    createMut.mutate(values, {
      onSuccess: (created) => navigate(`/stock-lots/${created.id}`),
    });
  });

  return (
    <FormBody
      mode="create"
      form={form}
      title={t('stockLot.form.createTitle')}
      onSubmit={onSubmit}
      submitting={createMut.isPending}
      submitLabel={t('stockLot.form.submitCreate')}
      cancelTo="/stock-lots"
      errorMessage={errorMessage}
    />
  );
}

function EditBranch({ existing }: { existing: StockLot }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const updateMut = useUpdateStockLot(existing.id);
  const { data: part } = usePart(existing.partId);
  const { data: vessel } = useVessel(existing.vesselId);

  const initial: StockLotUpdateValues = {
    minStockOverride: existing.minStockOverride,
    maxStockOverride: existing.maxStockOverride,
    notes: existing.notes,
  };

  const form = useForm<StockLotUpdateValues>({
    resolver: zodResolver(stockLotUpdateSchema),
    defaultValues: initial,
  });

  useEffect(() => {
    form.reset(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existing.id]);

  const errorMessage = (() => {
    if (!updateMut.error) return null;
    if (updateMut.error instanceof ApiError) {
      return t(updateMut.error.key, {
        defaultValue: t('stockLot.form.saveError'),
        ...(updateMut.error.params ?? {}),
      });
    }
    return t('stockLot.form.saveError');
  })();

  const onSubmit = form.handleSubmit((values) => {
    updateMut.mutate(values, {
      onSuccess: (updated) => navigate(`/stock-lots/${updated.id}`),
    });
  });

  return (
    <FormBody
      mode="edit"
      form={form}
      existing={existing}
      partLabel={
        part ? `${part.code} — ${part.name}` : existing.partId
      }
      vesselLabel={vessel ? vesselDisplayName(vessel) : existing.vesselId}
      title={t('stockLot.form.editTitle')}
      onSubmit={onSubmit}
      submitting={updateMut.isPending}
      submitLabel={t('stockLot.form.submitUpdate')}
      cancelTo={`/stock-lots/${existing.id}`}
      errorMessage={errorMessage}
    />
  );
}

type FormBodyProps =
  | {
      mode: 'create';
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      form: ReturnType<typeof useForm<any>>;
      existing?: never;
      partLabel?: never;
      vesselLabel?: never;
      title: string;
      onSubmit: (e?: React.BaseSyntheticEvent) => unknown;
      submitting: boolean;
      submitLabel: string;
      cancelTo: string;
      errorMessage: string | null;
    }
  | {
      mode: 'edit';
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      form: ReturnType<typeof useForm<any>>;
      existing: StockLot;
      partLabel: string;
      vesselLabel: string;
      title: string;
      onSubmit: (e?: React.BaseSyntheticEvent) => unknown;
      submitting: boolean;
      submitLabel: string;
      cancelTo: string;
      errorMessage: string | null;
    };

function FormBody(props: FormBodyProps) {
  const { t } = useTranslation();
  const {
    title,
    onSubmit,
    submitting,
    submitLabel,
    cancelTo,
    errorMessage,
    mode,
    form,
  } = props;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const control = form.control as Control<any>;

  return (
    <div className="space-y-6">
      <header className="flex items-center gap-2">
        <Button
          asChild
          variant="ghost"
          size="icon"
          aria-label={t('common.back')}
        >
          <Link to={cancelTo}>
            <ArrowLeft aria-hidden />
          </Link>
        </Button>
        <h1 className="text-2xl font-semibold">{title}</h1>
      </header>

      {mode === 'edit' && (
        <Alert>
          <Info className="h-4 w-4" aria-hidden />
          <AlertDescription>
            {t('stockLot.form.immutableHint')}
          </AlertDescription>
        </Alert>
      )}

      {mode === 'create' && (
        <Alert>
          <Info className="h-4 w-4" aria-hidden />
          <AlertDescription>{t('stockLot.form.zeroQtyHint')}</AlertDescription>
        </Alert>
      )}

      {errorMessage && (
        <Alert variant="destructive">
          <AlertTitle>{t('stockLot.form.saveError')}</AlertTitle>
          <AlertDescription>{errorMessage}</AlertDescription>
        </Alert>
      )}

      <Form {...form}>
        <form onSubmit={onSubmit} className="space-y-4" noValidate>
          {/* Identity (create-only fields are immutable on edit) */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                {t('stockLot.detail.sectionIdentity')}
              </CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              {mode === 'create' ? (
                <>
                  <FormField
                    control={control}
                    name="partId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('stockLot.fields.part')}</FormLabel>
                        <FormControl>
                          <PartPicker
                            value={field.value || null}
                            onChange={(v) => field.onChange(v ?? '')}
                            allowEmpty
                            emptyLabel={t('stockLot.form.partPlaceholder')}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={control}
                    name="vesselId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('stockLot.fields.vessel')}</FormLabel>
                        <FormControl>
                          <VesselSelect
                            value={field.value || null}
                            onChange={(v) => field.onChange(v ?? '')}
                            allowEmpty
                            emptyLabel={t(
                              'stockLot.form.vesselPlaceholder',
                            )}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </>
              ) : (
                <>
                  <ReadOnlyField
                    label={t('stockLot.fields.part')}
                    value={
                      <span className="font-mono">{props.partLabel}</span>
                    }
                  />
                  <ReadOnlyField
                    label={t('stockLot.fields.vessel')}
                    value={props.vesselLabel}
                  />
                </>
              )}
              {mode === 'create' ? (
                <div className="sm:col-span-2">
                  <TextField
                    control={control}
                    name="location"
                    label={t('stockLot.fields.location')}
                    hint={t('stockLot.form.locationHint')}
                  />
                </div>
              ) : (
                <ReadOnlyField
                  label={t('stockLot.fields.location')}
                  value={props.existing.location}
                />
              )}
              <div className="sm:col-span-2">
                <TextField
                  control={control}
                  name="notes"
                  label={t('stockLot.fields.notes')}
                  textarea
                />
              </div>
            </CardContent>
          </Card>

          {/* Thresholds (editable in both modes) */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                {t('stockLot.detail.sectionThresholds')}
              </CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              <TextField
                control={control}
                name="minStockOverride"
                label={t('stockLot.fields.minStockOverride')}
                type="number"
                hint={t('stockLot.form.minOverrideHint')}
              />
              <TextField
                control={control}
                name="maxStockOverride"
                label={t('stockLot.fields.maxStockOverride')}
                type="number"
                hint={t('stockLot.form.maxOverrideHint')}
              />
            </CardContent>
          </Card>

          {/* Footer */}
          <div className="flex justify-end gap-2 pt-2">
            <Button asChild variant="ghost" disabled={submitting}>
              <Link to={cancelTo}>{t('common.cancel')}</Link>
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? (
                <Loader2 className={cn('animate-spin')} aria-hidden />
              ) : (
                <Save aria-hidden />
              )}
              {submitLabel}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}

export function StockLotFormPage() {
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const [searchParams] = useSearchParams();
  const { data: existing, isLoading } = useStockLot(id);

  if (isEdit && isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-9 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (isEdit && existing) {
    return <EditBranch existing={existing} />;
  }

  return (
    <CreateBranch
      defaultPartId={searchParams.get('partId')}
      defaultVesselId={searchParams.get('vesselId')}
    />
  );
}
